/**
 * R6B — pure logic for the audited manual archive dialog.
 *
 * The dialog is a stateful component but its validation, composition,
 * and length rules are pure. Extracting them lets us Jest-test them
 * without a DOM harness. NO React, NO DOM imports here.
 */

/** The exact literal the R6A endpoint requires. */
export const CONFIRM_LITERAL = "ARCHIVE-JOB";

/** The endpoint path (matches admin_routes.py @admin_router.post("/jobs/archive")). */
export const ARCHIVE_ENDPOINT = "/admin/jobs/archive";

/** The R6A hard limit on archive_reason. */
export const MAX_ARCHIVE_REASON_LENGTH = 240;

/** Separator injected between preset and note. */
export const REASON_SEPARATOR = ": ";

/**
 * The six preset reasons, in the required order. The literal string
 * "Other" is used to identify the "requires a note" case.
 */
export const PRESET_REASONS = Object.freeze([
  "Customer canceled",
  "Duplicate job",
  "Scheduling error",
  "Service never performed",
  "Incorrect completion record",
  "Other",
]);

/** The typed-confirmation phrase required when archiving a completed job. */
export const COMPLETED_CONFIRM_PHRASE = "COMPLETED";

/**
 * Compose the archive_reason string sent to the backend.
 *
 * Rules:
 *   - No note (empty/whitespace-only)  -> "<preset>"
 *   - With note                        -> "<preset>: <note>"
 *
 * NEVER truncates. Callers must gate on {@link isSubmitReady} before
 * calling this (or accept a value that may exceed the maximum, which
 * they must then reject).
 */
export function composeArchiveReason(preset, note) {
  const p = typeof preset === "string" ? preset : "";
  const n = typeof note === "string" ? note : "";
  const trimmedNote = n.trim();
  if (trimmedNote === "") return p;
  return `${p}${REASON_SEPARATOR}${n}`;
}

/**
 * Maximum note length allowed for a given preset so that the composed
 * string is at most MAX_ARCHIVE_REASON_LENGTH characters. Never
 * negative. Handles unknown / empty preset defensively.
 */
export function maxNoteLength(preset) {
  const p = typeof preset === "string" ? preset : "";
  const budget = MAX_ARCHIVE_REASON_LENGTH - p.length - REASON_SEPARATOR.length;
  return Math.max(0, budget);
}

/** True when the given note is empty or whitespace only. */
export function isBlankNote(note) {
  return typeof note !== "string" || note.trim() === "";
}

/**
 * The single source of truth for "is the Archive button enabled?".
 *
 * Preconditions checked here:
 *   1. Preset MUST be one of PRESET_REASONS.
 *   2. If preset === "Other", note MUST be non-blank.
 *   3. Composed archive_reason MUST be <= MAX_ARCHIVE_REASON_LENGTH.
 *   4. If requireCompletedGate is true, completedInput MUST equal
 *      COMPLETED_CONFIRM_PHRASE EXACTLY, case-sensitive, no trim.
 *   5. If a request is already in flight, disabled.
 */
export function isSubmitReady({
  preset,
  note,
  requireCompletedGate,
  completedInput,
  inFlight,
}) {
  if (inFlight) return false;
  if (!PRESET_REASONS.includes(preset)) return false;
  if (preset === "Other" && isBlankNote(note)) return false;

  const composed = composeArchiveReason(preset, note);
  if (composed.length > MAX_ARCHIVE_REASON_LENGTH) return false;

  if (requireCompletedGate) {
    // Case-SENSITIVE exact match. No trim. No case folding.
    if (completedInput !== COMPLETED_CONFIRM_PHRASE) return false;
  }
  return true;
}

/**
 * Build the exact request body sent to POST /api/admin/jobs/archive.
 * Exactly THREE fields, no others. archived_by is NEVER included.
 */
export function buildArchiveRequestBody({ jobId, preset, note }) {
  return {
    job_id: jobId,
    archive_reason: composeArchiveReason(preset, note),
    confirm: CONFIRM_LITERAL,
  };
}

/**
 * Map an axios success response to a user-facing message. Success or
 * non-success is determined by ``body.result``.
 */
export const RESULT_MESSAGES = Object.freeze({
  ARCHIVED: "Job archived.",
  ALREADY_ARCHIVED: "Already archived by someone or something else.",
  RACE_NO_MATCH:
    "The job changed while archiving; nothing was written. Refresh and retry.",
  POST_WRITE_VERIFICATION_FAILED:
    "The write could not be verified. Do not retry — contact support.",
});

/**
 * True only for the ARCHIVED result code. Callers may then close the
 * dialog and refetch the currently mounted Jobs data. Every other
 * result — including ALREADY_ARCHIVED, RACE_NO_MATCH, and
 * POST_WRITE_VERIFICATION_FAILED — must NOT trigger any job-record
 * mutation, list mutation, cache invalidation, success close, or
 * success refresh.
 */
export function isArchivedSuccess(responseBody) {
  return (
    responseBody != null
    && typeof responseBody === "object"
    && responseBody.result === "ARCHIVED"
  );
}
